import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

export function ToolGrid({ tools }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tool-grid">
      {tools.map((t) => (
        <Link
          key={t.to}
          to={t.to}
          data-testid={`tool-${t.id}`}
          className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-blue-200 hover:shadow-sm transition-all flex flex-col"
        >
          <div className="flex items-start justify-between">
            <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${t.tone || "bg-blue-50 text-blue-600"}`}>
              <t.icon className="w-4 h-4" />
            </span>
            <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-slate-700 transition-colors" />
          </div>
          <h3 className="mt-4 font-display text-base font-semibold text-slate-900">{t.label}</h3>
          {t.description && <p className="mt-1 text-sm text-slate-600 leading-relaxed">{t.description}</p>}
          {t.badge != null && (
            <div className="mt-4 inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.16em] font-bold text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 self-start">
              {t.badge}
            </div>
          )}
        </Link>
      ))}
    </div>
  );
}

export function ToolPageShell({ eyebrow, title, intro, actions, children }) {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          {eyebrow && <div className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600 mb-2">{eyebrow}</div>}
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900 leading-[1.05]">{title}</h1>
          {intro && <p className="mt-2 text-slate-600 max-w-2xl">{intro}</p>}
        </div>
        {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

export const inputCls =
  "w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-colors";

export function Field({ label, required, children, hint }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-700 mb-1.5">
        {label} {required && <span className="text-blue-600">*</span>}
      </span>
      {children}
      {hint && <span className="block text-[11px] text-slate-500 mt-1">{hint}</span>}
    </label>
  );
}

const STATUS_TONE = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-100",
  draft: "bg-amber-50 text-amber-700 border-amber-100",
  archived: "bg-slate-100 text-slate-500 border-slate-200",
  paused: "bg-slate-100 text-slate-500 border-slate-200",
  pending: "bg-slate-100 text-slate-700 border-slate-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-100",
  rejected: "bg-slate-100 text-slate-500 border-slate-200",
  new: "bg-blue-50 text-blue-700 border-blue-100",
  contacted: "bg-amber-50 text-amber-700 border-amber-100",
  enrolled: "bg-emerald-50 text-emerald-700 border-emerald-100",
  declined: "bg-slate-100 text-slate-500 border-slate-200",
};

export function StatusPill({ status }) {
  const cls = STATUS_TONE[status] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span className={`inline-flex items-center text-[10.5px] uppercase tracking-[0.16em] font-bold rounded-full px-2 py-0.5 border ${cls}`}>
      {status}
    </span>
  );
}
